import { useState } from "react";
import {
  Box,
  Flex,
  Grid,
  IconButton,
  Text,
  AbsoluteCenter,
  Button,
  Spinner,
  Table,
  Skeleton,
  LinkBox,
  LinkOverlay,
  Collapsible,
} from "@chakra-ui/react";
import {
  MdArrowDownward,
  MdArrowUpward,
  MdHighlightOff,
  MdAdd,
  MdPhotoCamera,
} from "react-icons/md";
import { useParams, Link } from "react-router-dom";

import {
  PageContent,
  PageHeader,
  PageLayout,
  PageTitle,
} from "@/components/page";
import { EmptyState } from "@/components/ui/empty-state";
import {
  useFormatOutplantingDate,
  useObservationDates,
  useObservationStats,
  useRack,
  useRackObservations,
  useRackOutplanting,
  useTableView,
} from "@/hooks";
import { LoadingIndicator } from "@/components/LoadingIndicator";
import {
  BarChartBox,
  GrowthZoneBox,
  LineChartBox,
  ObservationsCalendar,
  TimespanFilter,
} from "@/components/charts";
import {
  ITimespan,
  TIMESPAN_OPTIONS,
  getTimespanInterval,
} from "@/components/charts/timespan";
import InsightCard from "@/components/InsightCard";
import PageSectionTitle from "@/components/page/PageSectionTitle";
import HealthIssueCell from "@/components/HealthIssueCell";
import { IObservation } from "@/types/data";
import { GrowthZoneCell } from "@/components/charts";
import SummaryCard from "@/components/SummaryCard";
import ObservationStatus from "@/components/ObservationStatus";
import { CollapsibleTrigger } from "@/components";
import TableViewToggle from "@/components/TableViewToggle";

type RouteParams = {
  nurseryId: string;
  tankId: string;
  rackId: string;
};

function RackDetail() {
  const { nurseryId, tankId, rackId } = useParams<RouteParams>();
  const { view } = useTableView();
  const [open, setOpen] = useState(true);
  const { data, isPending, error } = useRack(nurseryId!, tankId!, rackId!);
  const { data: rackOutplanting, error: rackOutplantingError } = useRackOutplanting(nurseryId!, rackId!);
  const { outplantingDate, description } = useFormatOutplantingDate(rackOutplanting, rackOutplantingError);

  const {
    data: observations,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useRackObservations(nurseryId!, rackId!);
  const [timespan, setTimespan] = useState<ITimespan>("Month");
  const { data: obsDates, isPending: obsDatesIsPending } = useObservationDates(
    nurseryId!,
    new URLSearchParams({ rack: rackId! }),
  );
  const { data: obsStats, isPending: obsStatsIsPending } = useObservationStats(
    nurseryId!,
    getTimespanInterval(timespan),
    new URLSearchParams({
      rack: rackId!,
      created_after: TIMESPAN_OPTIONS[timespan],
    }),
  );
  const latestObservation = observations?.pages[0]?.results[0];

  return (
    <PageLayout>
      <PageHeader backLink={`/nurseries/${nurseryId}/tanks/${tankId}`}>
        Rack {data?.name}
      </PageHeader>
      <PageTitle rackName={data?.name} isPending={isPending}>
        <Button
          hideBelow="sm"
          ml="auto"
          variant="solid"
          colorPalette="blue"
          size="xs"
          asChild
        >
          <Link to="observation/">
            <MdAdd />
            New Observation
          </Link>
        </Button>
      </PageTitle>
      <PageContent>
        <Collapsible.Root defaultOpen onOpenChange={(e) => setOpen(e.open)}>
          <Flex
            alignItems="end"
            w="full"
            justifyContent="space-between"
            mb={{ base: 4, sm: 6 }}
            flexWrap="wrap"
          >
            <TimespanFilter timespan={timespan} setTimespan={setTimespan} />
            <CollapsibleTrigger open={open} />
          </Flex>
          <Collapsible.Content>
            <Grid
              templateColumns="repeat(auto-fit, minmax(8rem, 1fr))"
              gap="2"
              mb="4"
            >
              <>
                <InsightCard
                  label="Total Fragments"
                  value={
                    observations?.pages[0]?.results[0]?.annotations?.length || 0
                  }
                />
                <InsightCard
                  label="Average Growth"
                  value={
                    obsStats?.length && obsStats[0].average_area
                      ? `${(((obsStats[obsStats.length - 1].average_area - obsStats[0].average_area) / obsStats[0].average_area) * 100).toFixed(1)}%`
                      : "0%"
                  }
                />
                <InsightCard
                  label="Est. Outplanting"
                  value={outplantingDate || "--"}
                  note={description}
                  styleState={rackOutplantingError ? "error" : "info"}
                />
              </>
            </Grid>
            <Grid
              templateColumns={{
                base: "1fr",
                sm: "repeat(auto-fit, minmax(18rem, 1fr))",
                md: "20rem repeat(auto-fit, minmax(14rem, 1fr))",
              }}
              gap="2"
              mb="4"
            >
              <Skeleton loading={obsDatesIsPending}>
                <ObservationsCalendar
                  activeDates={
                    obsDates?.map((obs) =>
                      new Date(obs.date).toLocaleDateString(),
                    ) || []
                  }
                  outplantingDate={rackOutplanting?.outplant_date}
                />
              </Skeleton>
              <Skeleton loading={obsStatsIsPending}>
                <LineChartBox
                  title="Aggregate Rack Area"
                  info="The area of all fragments over time"
                  data={obsStats || []}
                  outplantingDate={rackOutplanting?.outplant_date}
                  interval={getTimespanInterval(timespan)}
                />
              </Skeleton>
              <Skeleton loading={obsStatsIsPending}>
                <BarChartBox
                  title="Rack Health Issues"
                  info="Most prevalent health issues in the rack"
                  data={obsStats || []}
                />
              </Skeleton>
              <Skeleton loading={isFetching}>
                {latestObservation &&
                <GrowthZoneBox
                  title="Rack Growth Zones"
                  fragments_count_0_25={latestObservation.fragments_count_0_25}
                  fragments_count_25_50={latestObservation.fragments_count_25_50}
                  fragments_count_50_75={latestObservation.fragments_count_50_75}
                  fragments_count_75_100={latestObservation.fragments_count_75_100}
                  outplantingDate={outplantingDate}
                />}
              </Skeleton>
            </Grid>
          </Collapsible.Content>
        </Collapsible.Root>

        {error && (
          <EmptyState
            icon={<MdHighlightOff />}
            title="Error"
            description={error.message}
          />
        )}
        <Flex
          mb="1"
          w="full"
          alignItems="center"
          justifyContent="space-between"
        >
          <PageSectionTitle>Observations</PageSectionTitle>
          <TableViewToggle />
        </Flex>
        {view === "list" && (
          <Table.Root
            interactive
            colorPalette="blue"
            variant="line"
            stickyHeader
          >
            <Table.Header
              textTransform="uppercase"
              fontSize="xs"
              letterSpacing="0.75px"
            >
              <Table.Row
                css={{ "& > *": { color: "gray.400", fontWeight: "semibold" } }}
              >
                <Table.ColumnHeader>Date</Table.ColumnHeader>
                <Table.ColumnHeader textAlign="left">
                  Label Status
                </Table.ColumnHeader>
                <Table.ColumnHeader hideBelow="sm" textAlign="right">
                  Fragments
                </Table.ColumnHeader>
                <Table.ColumnHeader hideBelow="sm" textAlign="center">
                  Growth Zones
                </Table.ColumnHeader>
                <Table.ColumnHeader textAlign="right">
                  Unhealthy Fragments
                </Table.ColumnHeader>
                <Table.ColumnHeader textAlign="right">
                  Growth
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {observations?.pages.map((page) =>
                page.results.map((obs: IObservation) => (
                  <LinkBox
                    key={obs.id}
                    position="relative"
                    as={Table.Row}
                    transform="scale(1)"
                    _hover={{ bg: "blue.50" }}
                  >
                    <Table.Cell
                      fontSize={{ base: "xs", sm: "sm" }}
                      fontWeight="bold"
                      color="blue.500"
                      lineHeight="1.125"
                    >
                      <LinkOverlay asChild>
                        <Link to={`observation/${obs.id}/`}>
                          {new Date(obs.created).toLocaleDateString()}
                          <Text fontSize="xs" color="fg" fontWeight="400">
                            {new Date(obs.created).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </Text>
                        </Link>
                      </LinkOverlay>
                    </Table.Cell>
                    <Table.Cell textAlign="left">
                      <ObservationStatus status={obs.status} />
                    </Table.Cell>
                    <Table.Cell hideBelow="sm" textAlign="right">
                      {obs.annotations?.length || "-"}
                    </Table.Cell>
                    <Table.Cell hideBelow="sm">
                      <GrowthZoneCell
                        fragments_count_0_25={obs.fragments_count_0_25}
                        fragments_count_25_50={obs.fragments_count_25_50}
                        fragments_count_50_75={obs.fragments_count_50_75}
                        fragments_count_75_100={obs.fragments_count_75_100}
                      />
                    </Table.Cell>
                    <Table.Cell
                      fontSize={{ base: "xs", sm: "sm" }}
                      textAlign="right"
                    >
                      {obs.status === "processing" ? (
                        "-"
                      ) : (
                        <HealthIssueCell
                          fragments={obs.annotations?.length || 0}
                          unhealthy_fragments={
                            obs.annotations?.filter((a) => a.bodies.length)
                              .length || 0
                          }
                        />
                      )}
                    </Table.Cell>
                    <Table.Cell
                      textAlign="right"
                      fontSize={{ base: "xs", sm: "sm" }}
                    >
                      {obs.growth !== null
                        ? `${Number(obs.growth).toFixed(1).replace(/\.0$/, "")}%`
                        : "-"}
                      <Text display="inline-block" pl="1">
                        {obs.growth > 0 && <MdArrowUpward />}
                        {obs.growth < 0 && <MdArrowDownward />}
                      </Text>
                    </Table.Cell>
                  </LinkBox>
                )),
              )}
            </Table.Body>
          </Table.Root>
        )}
        {view === "grid" && (
          <Grid
            templateColumns={{
              base: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
            }}
            gap={[2, 4]}
            mb="4"
          >
            {observations?.pages.map((page) =>
              page.results.map((obs: IObservation) => (
                <SummaryCard
                  key={obs.id}
                  linkTo={`observation/${obs.id}/`}
                  title={new Date(obs.created).toLocaleDateString()}
                  subtitle={new Date(obs.created).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                  status={obs.status}
                  fragments={obs.annotations?.length ?? 0}
                  unhealthyFragments={
                    obs.annotations?.filter((a) => a.bodies.length).length ?? 0
                  }
                  growth={obs.growth}
                  fragments_count_0_25={obs.fragments_count_0_25}
                  fragments_count_25_50={obs.fragments_count_25_50}
                  fragments_count_50_75={obs.fragments_count_50_75}
                  fragments_count_75_100={obs.fragments_count_75_100}
                />
              )),
            )}
          </Grid>
        )}
        <Flex flexDir="column" gap="2" mt="3">
          {isFetching || (isFetchingNextPage && <LoadingIndicator />)}
          {hasNextPage && (
            <Button
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              colorPalette="blue"
              variant="outline"
              size="sm"
              alignSelf="center"
              mb="4"
            >
              <Spinner
                size="sm"
                display={isFetching || isFetchingNextPage ? "initial" : "none"}
              />
              {isFetching || isFetchingNextPage ? "Loading" : "Load more"}
            </Button>
          )}
        </Flex>
      </PageContent>
      <Box
        hideFrom="sm"
        zIndex="10"
        textAlign="center"
        position="fixed"
        bottom="0"
        left="0"
        right="0"
        bg="bg.info"
        pt="6"
        h="12"
        borderTopWidth="1px"
        borderColor="blue.100"
      >
        <AbsoluteCenter>
          <IconButton
            bottom="6"
            asChild
            aria-label="Take photo"
            rounded="full"
            size="xl"
            colorPalette="blue"
            variant="surface"
          >
            <Link to="observation/">
              <MdPhotoCamera />
            </Link>
          </IconButton>
        </AbsoluteCenter>
        <Text fontSize="xs" color="blue.700">
          New Observation
        </Text>
      </Box>
    </PageLayout>
  );
}

export default RackDetail;
